import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Tempo esgotado: ${label}`)), ms)
    ),
  ]);
}

const getSafeNextPath = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const next = queryParams.get("next")?.trim();

  if (!next || !next.startsWith("/") || next.startsWith("//")) {
    return "/";
  }

  return next;
};

const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    let settled = false;

    const fallbackTimeout = setTimeout(() => {
      if (!isMounted || settled) return;
      settled = true;
      toast.error("Não foi possível concluir a autenticação. Tente entrar novamente.");
      navigate("/auth", { replace: true });
    }, 15000);

    const processCallback = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

      const code = queryParams.get("code");
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const type = queryParams.get("type") ?? hashParams.get("type");
      const isRecovery = type === "recovery";

      console.info("[AuthCallback] params", {
        hasCode: Boolean(code),
        hasAccessToken: Boolean(accessToken),
        hasRefreshToken: Boolean(refreshToken),
        type,
      });

      try {
        if (code) {
          const { error } = await withTimeout(
            supabase.auth.exchangeCodeForSession(code),
            12000,
            "trocar código por sessão",
          );
          console.info("[AuthCallback] exchangeCodeForSession", {
            ok: !error,
            error: error?.message ?? null,
          });

          if (error) throw error;
        } else if (accessToken && refreshToken) {
          const { error } = await withTimeout(
            supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            }),
            12000,
            "definir sessão",
          );

          console.info("[AuthCallback] setSession", {
            ok: !error,
            error: error?.message ?? null,
          });

          if (error) throw error;
        }

        const {
          data: { session },
        } = await withTimeout(
          supabase.auth.getSession(),
          12000,
          "obter sessão",
        );

        console.info("[AuthCallback] session resolved", {
          hasSession: Boolean(session),
          userId: session?.user?.id ?? null,
        });

        if (!isMounted) return;

        if (!session) {
          if (isRecovery) {
            settled = true;
            navigate("/reset-password", { replace: true });
          } else {
            settled = true;
            navigate("/auth", { replace: true });
          }
          return;
        }

        if (isRecovery) {
          settled = true;
          navigate("/reset-password?recovery=1", { replace: true });
          return;
        }

        const nextPath = getSafeNextPath();
        settled = true;
        navigate(nextPath, { replace: true });
      } catch (error) {
        console.error("[AuthCallback] callback failed", error);

        if (!isMounted) return;
        toast.error("Não foi possível concluir a autenticação. Tente entrar novamente.");
        if (isRecovery) {
          settled = true;
          navigate("/reset-password", { replace: true });
        } else {
          settled = true;
          navigate("/auth", { replace: true });
        }
      } finally {
        settled = true;
        clearTimeout(fallbackTimeout);
      }
    };

    void processCallback();

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimeout);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Processando autenticação...</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallback;